import { useTranslation } from 'react-i18next';

import { OpList } from 'new-components/DLS/OpList/OpList';
import { OpTypography } from 'new-components/DLS/OpTypography/OpTypography';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';

const { Text, Title } = OpTypography;

interface ExternalRedFlagDetailsModalProps {
  open: boolean;
  onClose: () => void;
  record: ExternalRedFlagRecord | null;
}

interface ExternalRedFlagPerson {
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  gender?: string;
  dob?: string;
  photo_urls?: string[];
}

interface ExternalRedFlagCase {
  arrest_date?: string;
  charges?: Array<{
    type?: string;
  }>;
}

interface ExternalRedFlagSource {
  name?: string;
  county?: string;
  state?: string;
}

interface ExternalRedFlagRecord {
  person?: ExternalRedFlagPerson;
  category?: string;
  cases?: ExternalRedFlagCase[];
  source?: ExternalRedFlagSource;
}

const ExternalRedFlagDetailsModal = ({
  open,
  onClose,
  record,
}: ExternalRedFlagDetailsModalProps) => {
  const { t } = useTranslation();

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return null;
    try {
      return dateStr.length === 8
        ? dateStr.replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3') // e.g. 19960600 will be converted to 1996-06-00
        : dateStr;
    } catch {
      return null;
    }
  };

  const isValidValue = (value: unknown) =>
    value !== null && value !== undefined && value !== '';

  const profileData = [
    {
      label: t('visitors.externalRedFlag.modal.fields.firstName'),
      value: record?.person?.first_name,
    },
    {
      label: t('visitors.externalRedFlag.modal.fields.lastName'),
      value: record?.person?.last_name,
    },
    {
      label: t('visitors.externalRedFlag.modal.fields.middleName'),
      value: record?.person?.middle_name,
    },
    {
      label: t('visitors.externalRedFlag.modal.fields.gender'),
      value: record?.person?.gender,
    },
    {
      label: t('visitors.externalRedFlag.modal.fields.dob'),
      value: formatDate(record?.person?.dob),
    },
  ].filter((item) => isValidValue(item.value));

  const recordDetails = [
    {
      label: t('visitors.externalRedFlag.modal.fields.category'),
      value: record?.category,
    },
    {
      label: t('visitors.externalRedFlag.modal.fields.date'),
      value: formatDate(record?.cases?.[0]?.arrest_date),
    },
    {
      label: t('visitors.externalRedFlag.modal.fields.type'),
      value: record?.cases?.[0]?.charges?.[0]?.type,
    },
    {
      label: t('visitors.externalRedFlag.modal.fields.source'),
      value: record?.source?.name,
    },
    {
      label: t('visitors.externalRedFlag.modal.fields.sourceCounty'),
      value: record?.source?.county,
    },
    {
      label: t('visitors.externalRedFlag.modal.fields.sourceState'),
      value: record?.source?.state,
    },
  ].filter((item) => isValidValue(item.value));

  return (
    <OpModal
      title={t('visitors.externalRedFlag.modal.title')}
      open={open}
      footer={[
        <OpButton key="close" type="primary" onClick={onClose}>
          {t('visitors.externalRedFlag.modal.close')}
        </OpButton>,
      ]}
      onCancel={onClose}
      width={720}
    >
      <div
        style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden' }}
      >
        {profileData.length > 0 && (
          <>
            <Title level={5} style={{ marginBottom: 16 }}>
              {t('visitors.externalRedFlag.modal.personalInfo')}
            </Title>
            <OpList
              bordered
              dataSource={profileData}
              renderItem={(item) => {
                const profileItem = item as { label: string; value: string };
                return (
                  <OpList.Item>
                    <Text strong>{profileItem.label}:</Text> {profileItem.value}
                  </OpList.Item>
                );
              }}
            />
          </>
        )}

        {recordDetails.length > 0 && (
          <>
            <Title level={5} style={{ margin: '24px 0 16px' }}>
              {t('visitors.externalRedFlag.modal.recordDetails')}
            </Title>
            <OpList
              bordered
              dataSource={recordDetails}
              renderItem={(item) => {
                const detailItem = item as { label: string; value: string };
                return (
                  <OpList.Item>
                    <Text strong>{detailItem.label}:</Text> {detailItem.value}
                  </OpList.Item>
                );
              }}
            />
          </>
        )}

        {(record?.person?.photo_urls?.length || 0) > 0 && (
          <>
            <Title level={5} style={{ margin: '24px 0 16px' }}>
              {t('visitors.externalRedFlag.modal.photoUrls')}
            </Title>
            <OpList
              bordered
              dataSource={record?.person?.photo_urls}
              renderItem={(item) => {
                const url = item as string;
                return (
                  <OpList.Item>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      {url}
                    </a>
                  </OpList.Item>
                );
              }}
            />
          </>
        )}
      </div>
    </OpModal>
  );
};

export default ExternalRedFlagDetailsModal;
